import {db} from './firebase';
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    increment,
    limit,
    orderBy,
    query,
    setDoc,
    Timestamp,
    updateDoc,
    where
} from "firebase/firestore";
import {Author, Book, BookCreate, Genres} from "@/app/types";
import bcrypt from 'bcryptjs';
import jwt from 'jsonwebtoken';
import {toast} from "@/hooks/use-toast";

// Autenticación

const SECRET_KEY = process.env.JWT_SECRET || 'mandaloasinoma';

const generateVerificationCode = () => Math.floor(100000 + Math.random() * 900000).toString();

const sendVerificationEmail = async (email: string, code: string) => {

};

// Función para registrar un nuevo usuario
export const registerUser = async (nickname: string, email: string, password: string): Promise<boolean> => {
    try {
        // Verifica si el correo ya está registrado
        const emailRef = doc(db, 'users', email); // El correo será el ID del documento
        const emailSnap = await getDoc(emailRef);
        if (emailSnap.exists()) {
            throw new Error('Ya existe un usuario con ese correo');
        }

        // Verifica si el nickname ya está registrado
        const nicknameQuery = query(collection(db, 'users'), where('nickname', '==', nickname));
        const nicknameSnap = await getDocs(nicknameQuery);
        if (!nicknameSnap.empty) {
            throw new Error('Ya existe un usuario con ese nickname');
        }

        // Hash de la contraseña
        const hashedPassword = await bcrypt.hash(password, 10);

        // Registra al usuario
        const newUser = {
            nickname,
            email,
            password: hashedPassword,
            role: 'user',
            favorites: [],
            dateCreated: new Date().toISOString(),
        };

        // Crea el documento con el ID como el correo del usuario
        await setDoc(emailRef, newUser);  // Usamos setDoc para crear el usuario con el correo como ID

        console.log(`Usuario registrado con correo: ${email}`);
        return true;
    } catch (error) {
        console.error("Error registrando usuario:", error);
        throw error;
    }
};

interface LoginReponse{
    role: string;
    email: string;
    nickname: string;
}

// Función para iniciar sesión
export const loginUser = async (email: string, password: string): Promise<LoginReponse> => {
    try {
        const userRef = doc(db, 'users', email);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
            toast({
                title: "Error",
                description: "El correo no está registrado",
                variant: "destructive",
            })
            throw new Error('El correo no está registrado');
        }

        const userData = userSnap.data();
        const isValidPassword = await bcrypt.compare(password, userData.password);

        if (!isValidPassword) {
            toast({
                title: "Error",
                description: "Contraseña incorrecta",
                variant: "destructive",
            })
            throw new Error('Contraseña incorrecta');
        }

        // Generar un token JWT
        return {
            role: userData.role,
            email: userData.email,
            nickname: userData.nickname,
        };
    } catch (error) {
        console.error("Error iniciando sesión:", error);
        throw error;
    }
};

interface UserResponse {
    email: string;
    nickname: string;
    role: string;
    favorites: string[];
    dateCreated: any;
    avatar: string;
}

// Función para obtener a todos los usuarios
export const getAllUsers = async (): Promise<UserResponse[]> => {
    try {
        const usersQuery = query(collection(db, 'users'));
        const querySnapshot = await getDocs(usersQuery);
        const users: any[] = [];

        querySnapshot.forEach((doc) => {
            const userData = doc.data();
            users.push({
                ...userData,
            });
        });

        return users;
    } catch (error) {
        console.error("Error obteniendo los usuarios:", error);
        throw error;
    }
};

// Función para obtener los datos de un usuario por su correo
export const getUserByEmail = async (email: string): Promise<UserResponse> => {
    try {
        const userRef = doc(db, 'users', email);
        const userSnap = await getDoc(userRef);

        console.log(userSnap.data())

        if (!userSnap.exists()) {
            throw new Error('El correo no está registrado');
        }

        return userSnap.data() as UserResponse;
    } catch (error) {
        console.error("Error obteniendo el usuario:", error);
        throw error;
    }
};

// Función para eliminar la cuenta de un usuario
export const deleteUserAccount = async (adminToken: string, emailToDelete: string): Promise<void> => {
    try {
        // 🔹 Decodificar el token del admin
        const decoded = jwt.verify(adminToken, SECRET_KEY) as { email: string; role: string };

        // 🔹 Verificar si el usuario es admin
        if (decoded.role !== 'admin') {
            throw new Error('No tienes permisos para eliminar usuarios');
        }

        // 🔹 Eliminar usuario de Firestore
        await deleteDoc(doc(db, 'users', emailToDelete));

        console.log(`✅ Cuenta eliminada: ${emailToDelete}`);
    } catch (error) {
        console.error("❌ Error eliminando cuenta:", error);
        throw error;
    }
};

export const sendPasswordResetEmail = async (email: string): Promise<void> => {
    try {
        // Generar un token de recuperación
        const resetToken = jwt.sign({ email }, SECRET_KEY, { expiresIn: '15m' });

        // Guardar el token en Firestore
        await setDoc(doc(db, 'passwordResets', email), { resetToken });


        console.log(`Correo de recuperación enviado a ${email}`);
    } catch (error) {
        console.error("Error enviando el correo de recuperación:", error);
        throw error;
    }
};

// Función para aumentar el contador de visitas de un libro
export const incrementVisits = async (id: string): Promise<void> => {
    try {
        const bookRef = doc(db, 'book', id); // Referencia al documento
        await updateDoc(bookRef, {
            visits: increment(1) // Incrementar el contador de visitas en 1
        });
    } catch (error) {
        console.error("Error incrementando las visitas:", error);
        throw error;
    }
};

export const getRecentBooks = async (): Promise<Book[]> => {
    try {
        const booksQuery = query(collection(db, 'book'), orderBy('publicationDate', 'desc'));
        const querySnapshot = await getDocs(booksQuery);
        const books: Book[] = []; // Aquí usamos la interfaz Book

        // No mostrar libros ocultos
        querySnapshot.forEach((doc) => {
            const bookData = doc.data();
            if (!bookData.isHidden) {
                books.push({
                    ...bookData,
                    id: doc.id,
                    publicationDate: bookData.publicationDate.toDate() // Asegúrate de convertirlo a Date si es un timestamp
                } as Book);
            }
        });

        // querySnapshot.forEach((doc) => {
        //     const bookData = doc.data();
        //     books.push({
        //         ...bookData,
        //         id: doc.id,
        //         publicationDate: bookData.publicationDate.toDate() // Asegúrate de convertirlo a Date si es un timestamp
        //     } as Book);
        // });

        return books;
    } catch (error) {
        console.error("Error obteniendo los libros recientes:", error);
        throw error;
    }
};

export const getRecentBooksAdmin = async (): Promise<Book[]> => {
    try {
        const booksQuery = query(collection(db, 'book'), orderBy('publicationDate', 'desc'));
        const querySnapshot = await getDocs(booksQuery);
        const books: Book[] = []; // Aquí usamos la interfaz Book

        querySnapshot.forEach((doc) => {
            const bookData = doc.data();
            books.push({
                ...bookData,
                id: doc.id,
                publicationDate: bookData.publicationDate.toDate() // Asegúrate de convertirlo a Date si es un timestamp
            } as Book);
        });

        return books;
    } catch (error) {
        console.error("Error obteniendo los libros recientes:", error);
        throw error;
    }
}

// Función para obtener libros populares
export const getPopularBooks = async (): Promise<Book[]> => {
    try {
        const booksQuery = query(collection(db, 'book'), orderBy('visits', 'desc'), limit(10)); // Asegúrate de que el campo 'views' existe
        const querySnapshot = await getDocs(booksQuery);
        const books: Book[] = []; // Aquí usamos la interfaz Book

        // No mostrar libros ocultos
        querySnapshot.forEach((doc) => {
            const bookData = doc.data();
            if (!bookData.isHidden) {
                books.push({
                    ...bookData,
                    id: doc.id,
                    publicationDate: bookData.publicationDate.toDate() // Asegúrate de convertirlo a Date si es un timestamp
                } as Book);
            }
        });

        // querySnapshot.forEach((doc) => {
        //     const bookData = doc.data();
        //     books.push({
        //         ...bookData,
        //         id: doc.id,
        //         publicationDate: bookData.publicationDate.toDate() // Asegúrate de convertirlo a Date si es un timestamp
        //     } as Book);
        // });

        return books;
    } catch (error) {
        console.error("Error obteniendo los libros populares:", error);
        throw error;
    }
};

export const getAllGenres = async (): Promise<Genres[]> => {
    try {
        const genresQuery = query(collection(db, 'genders'));
        const querySnapshot = await getDocs(genresQuery);
        const genres: Genres[] = [];

        querySnapshot.forEach((doc) => {
            const genreData = doc.data();
            genres.push({
                ...genreData,
                id: doc.id
            } as Genres);
        });

        return genres;
    } catch (error) {
        console.error("Error obteniendo los géneros:", error);
        throw error;
    }
}

export const getAllAuthors = async (): Promise<Author[]> => {
    try {
        const authorsQuery = query(collection(db, 'authors'));
        const querySnapshot = await getDocs(authorsQuery);
        const authors: Author[] = [];

        querySnapshot.forEach((doc) => {
            const authorData = doc.data();
            authors.push({
                ...authorData,
                id: doc.id
            } as Author);
        });

        return authors;
    } catch (error) {
        console.error("Error obteniendo los autores:", error);
        throw error;
    }
}

// Obtener todos los mangas, filtrados por tipo 'manga' y ordenados por fecha de publicación
export const getMangas = async (): Promise<Book[]> => {
    try {
        const mangasQuery = query(collection(db, 'book'), orderBy('publicationDate', 'desc'));
        const querySnapshot = await getDocs(mangasQuery);
        const mangas: Book[] = []; // Aquí usamos la interfaz Book

        querySnapshot.forEach((doc) => {
            const bookData = doc.data();
            if (bookData.type === 'manga') {
                mangas.push({
                    ...bookData,
                    id: doc.id,
                    publicationDate: bookData.publicationDate.toDate() // Asegúrate de convertirlo a Date si es un timestamp
                } as Book);
            }
        });

        return mangas;
    } catch (error) {
        console.error("Error obteniendo los mangas:", error);
        throw error;
    }
};

// Obtener un manga por su ID
export const getManga = async (id: string): Promise<Book> => {
    try {
        const docRef = doc(db, 'book', id); // Referencia al documento
        const docSnapshot = await getDoc(docRef); // Obtener el snapshot del documento

        if (!docSnapshot.exists()) {
            throw new Error('Manga not found');
        }

        const mangaData = docSnapshot.data();
        return {
            ...mangaData,
            id: docSnapshot.id,
            publicationDate: mangaData.publicationDate.toDate() // Asegúrate de convertirlo a Date si es un timestamp
        } as Book;
    } catch (error) {
        console.error("Error obteniendo los mangas:", error);
        throw error;
    }
}

// Obtener los últimos 6 mangas
export const getSixLastMangas = async (): Promise<Book[]> => {
    try {
        const mangasQuery = query(
            collection(db, 'book'),
            orderBy('publicationDate', 'desc')
        );
        const querySnapshot = await getDocs(mangasQuery);
        const mangas: Book[] = []; // Arreglo para almacenar los mangas
        let count = 0;

        querySnapshot.forEach((doc) => {
            const bookData = doc.data(); // Asegurarse de que los datos son del tipo Book
            if (bookData.type === 'manga' && count < 6) {
                mangas.push({
                    ...bookData,
                    id: doc.id,
                    publicationDate: bookData.publicationDate.toDate() // Asegúrate de convertirlo a Date si es un timestamp
                } as Book);
                count++;
            }
        });

        return mangas; // Retornar los mangas obtenidos
    } catch (error) {
        console.error('Error getting documents:', error);
        throw new Error('Error getting mangas.'); // Lanzar error si ocurre un problema
    }
};

// Buscar mangas por nombre
export const getMangasByName = async (title: string): Promise<Book[]> => {
    try {
        const endTitle = title.substring(0, title.length - 1) +
            String.fromCharCode(title.charCodeAt(title.length - 1) + 1);

        const mangasQuery = query(
            collection(db, 'book'),
            where('title', '>=', title),
            where('title', '<', endTitle)
        );

        const querySnapshot = await getDocs(mangasQuery);

        const mangas: Book[] = []; // Aquí usamos la interfaz Book
        querySnapshot.forEach((doc) => {
            const mangaData = doc.data() as Book; // Asegúrate de que doc.data() cumpla con la interfaz Book
            if (mangaData.type === 'manga') {
                mangas.push({
                    ...mangaData,
                    id: doc.id // Incluir el id del documento
                });
            }
        });

        return mangas; // Devolver solo los mangas
    } catch (error) {
        console.error('Error getting documents:', error);
        return []; // Devuelve un array vacío en caso de error
    }
};

// Buscar mangas por etiqueta
export const getMangasByTag = async (tag: string): Promise<Book[]> => {
    try {
        const mangasQuery = query(
            collection(db, 'book'),
            where('genders', 'array-contains', tag)
        );

        const querySnapshot = await getDocs(mangasQuery);
        const mangas: Book[] = []; // Aquí usamos la interfaz Book

        querySnapshot.forEach((doc) => {
            const bookData = doc.data() as Book; // Asegúrate de que doc.data() cumpla con la interfaz Book
            if (bookData.type === 'manga') {
                mangas.push({
                    ...bookData,
                    id: doc.id // Incluir el id del documento
                });
            }
        });

        return mangas; // Devolver solo los mangas
    } catch (error) {
        console.error('Error getting documents:', error);
        return []; // Devuelve un array vacío en caso de error
    }
};

// Obtener todos los doujins
export const getDoujins = async (): Promise<Book[]> => {
    try {
        const doujinQuery = query(
            collection(db, 'book'),
            orderBy('publicationDate', 'desc')
        );

        const querySnapshot = await getDocs(doujinQuery);
        const doujins: Book[] = []; // Aquí usamos la interfaz Book

        querySnapshot.forEach((doc) => {
            const bookData = doc.data() as Book; // Asegúrate de que doc.data() cumpla con la interfaz Book
            if (bookData.type === 'doujin') {
                doujins.push({
                    ...bookData,
                    id: doc.id // Incluir el id del documento
                });
            }
        });

        return doujins; // Devolver solo los doujins
    } catch (error) {
        console.error('Error getting documents:', error);
        return []; // Devuelve un array vacío en caso de error
    }
};

// Obtener un doujin por su ID
export const getDoujin = async (id: string): Promise<Book | null> => {
    try {
        const docRef = doc(db, 'book', id); // Referencia al documento
        const docSnapshot = await getDoc(docRef); // Obtener el documento

        if (!docSnapshot.exists()) {
            return null; // Devuelve null si no se encuentra el doujin
        }

        return {
            ...docSnapshot.data(), // Retorna los datos del doujin
            id: docSnapshot.id // Incluir el id del documento
        } as Book; // Asegúrate de que los datos cumplan con la interfaz Book
    } catch (error) {
        console.error('Error getting doujin:', error);
        return null; // Devuelve null en caso de error
    }
};

// Obtener un book por su ID
export const getBook = async (id: string): Promise<Book | null> => {
    try {
        const docRef = doc(db, 'book', id); // Referencia al documento
        const docSnapshot = await getDoc(docRef); // Obtener el documento

        if (!docSnapshot.exists()) {
            return null; // Devuelve null si no se encuentra el book
        }

        // Si la variable 'isHidden' es true, no se debe mostrar el libro
        const bookData = docSnapshot.data() as Book;
        if (bookData.isHidden) {
            return null; // Devuelve null si el libro está oculto
        }

        return {
            ...docSnapshot.data(), // Retorna los datos del doujin
            id: docSnapshot.id // Incluir el id del documento
        } as Book; // Asegúrate de que los datos cumplan con la interfaz Book
    } catch (error) {
        console.error('Error getting book:', error);
        return null; // Devuelve null en caso de error
    }
};

export const getBookAdmin = async (id: string): Promise<Book | null> => {
    try {
        const docRef = doc(db, 'book', id); // Referencia al documento
        const docSnapshot = await getDoc(docRef); // Obtener el documento

        if (!docSnapshot.exists()) {
            return null; // Devuelve null si no se encuentra el book
        }

        return {
            ...docSnapshot.data(), // Retorna los datos del doujin
            id: docSnapshot.id // Incluir el id del documento
        } as Book; // Asegúrate de que los datos cumplan con la interfaz Book
    } catch (error) {
        console.error('Error getting book:', error);
        return null; // Devuelve null en caso de error
    }
}

// Obtener los últimos 6 doujins
export const getSixLastDoujins = async (): Promise<Book[]> => {
    try {
        const doujinsQuery = query(collection(db, 'book'), orderBy('publicationDate', 'desc'));
        const querySnapshot = await getDocs(doujinsQuery);
        const doujins: Book[] = [];

        querySnapshot.forEach((doc) => {
            const bookData = doc.data();
            if (bookData.type === 'doujin' && doujins.length < 6) {
                doujins.push({
                    ...bookData,
                    id: doc.id // Incluir el id del documento
                } as Book);
            }
        });

        return doujins;
    } catch (error) {
        console.error('Error getting doujins:', error);
        return []; // Devuelve un array vacío en caso de error
    }
};

// Buscar doujins por etiqueta
export const getDoujinsByTag = async (tag: string): Promise<Book[]> => {
    try {
        const doujinsQuery = query(collection(db, 'book'), where('genders', 'array-contains', tag));
        const querySnapshot = await getDocs(doujinsQuery);
        const doujins: Book[] = [];

        querySnapshot.forEach((doc) => {
            const bookData = doc.data();
            if (bookData.type === 'doujin') {
                doujins.push({
                    ...bookData,
                    id: doc.id // Incluir el id del documento
                } as Book);
            }
        });

        return doujins;
    } catch (error) {
        console.error('Error getting doujins by tag:', error);
        return []; // Devuelve un array vacío en caso de error
    }
};

export const createBook = async (bookData: BookCreate): Promise<boolean> => {
    try {
        // Convierte la fecha a Timestamp de Firestore
        const publicationDate = Timestamp.fromDate(new Date(bookData.publicationDate));

        // Define el nuevo documento sin ID
        const newBook = {
            ...bookData,
            publicationDate,
            visits: 200,
        };

        // Agrega el documento
        const docRef = await addDoc(collection(db, 'book'), newBook);

        // Actualiza el documento para incluir el ID generado dentro del campo `id`
        await updateDoc(doc(db, 'book', docRef.id), { id: docRef.id });

        console.log(`Documento añadido con ID: ${docRef.id}`);
        return true;
    } catch (error) {
        console.error("Error al agregar documento:", error);
        throw error;
    }
};

// Actualizar un libro existente
export const updateBook = async (id: string, newData: Partial<Book>): Promise<void> => {
    try {
        const bookRef = doc(db, 'book', id); // Referencia al documento específico
        await updateDoc(bookRef, newData);
    } catch (error) {
        console.error('Error updating book:', error);
        throw new Error('Error updating book.'); // Lanza un error si la actualización falla
    }
};

// Eliminar un libro por su ID
export const deleteBook = async (id: string): Promise<void> => {
    try {
        const bookRef = doc(db, 'book', id); // Referencia al documento específico
        await deleteDoc(bookRef); // Eliminar el documento
    } catch (error) {
        console.error('Error deleting book:', error);
        throw new Error('Error deleting book.'); // Lanza un error si la eliminación falla
    }
};

// Actualizar la visibilidad de un libro
export const updateBookVisibility = async (id: string, isHidden: boolean): Promise<void> => {
    try {
        const bookRef = doc(db, 'book', id); // Referencia al documento específico
        await updateDoc(bookRef, { isHidden }); // Actualizar el campo isAvailable
    } catch (error) {
        console.error('Error updating book visibility:', error);
        throw new Error('Error updating book visibility.');
    }
};